/**
 * Générateur — Comparer des volumes
 * Attendu ID : 648 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, texte_trou_saisie, relier
 * Labels de référence :
 *   - Comparer deux assemblages de cubes par dénombrement (SVG)
 * Variantes estimées : 500+
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 648;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_COMP = 'Comparer deux assemblages de cubes par dénombrement (SVG)';

// ─── Moteur SVG isométrique ───

const S = 28;       // demi-largeur d'une face de cube
const SH = S * 0.6; // ratio hauteur isométrique

/** Convertit position grille (col, row, layer) en coordonnées iso */
const isoPos = (c, r, l) => ({
    x: (c - r) * S,
    y: (c + r) * SH / 2 - l * SH,
});

/** Dessine un cube isométrique aux coordonnées grille */
const drawCube = (c, r, l) => {
    const { x, y } = isoPos(c, r, l);
    const top = `${x},${y - SH} ${x + S},${y - SH / 2} ${x},${y} ${x - S},${y - SH / 2}`;
    const left = `${x - S},${y - SH / 2} ${x},${y} ${x},${y + SH} ${x - S},${y + SH / 2}`;
    const right = `${x},${y} ${x + S},${y - SH / 2} ${x + S},${y + SH / 2} ${x},${y + SH}`;
    return `<polygon points="${top}" fill="#dbeafe" stroke="#1e293b" stroke-width="1"/>` +
           `<polygon points="${left}" fill="#93c5fd" stroke="#1e293b" stroke-width="1"/>` +
           `<polygon points="${right}" fill="#60a5fa" stroke="#1e293b" stroke-width="1"/>`;
};

/** Construit un SVG composite avec plusieurs assemblages côte à côte.
 *  Évite les problèmes de layout flex dans elements_additionnels (chaque image étant un block).
 *  Retourne un seul SVG = un seul fingerprint pour l'audit. */
const buildCompositeSVG = (assemblages, labels) => {
    const gap = 30;
    const pad = 15;
    const labelH = 22;

    const subs = assemblages.map((asm, i) => {
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (const { c, r, l } of asm.cubes) {
            const { x, y } = isoPos(c, r, l);
            minX = Math.min(minX, x - S);
            maxX = Math.max(maxX, x + S);
            minY = Math.min(minY, y - SH);
            maxY = Math.max(maxY, y + SH);
        }
        const sorted = [...asm.cubes].sort((a, b) => {
            if (a.l !== b.l) return a.l - b.l;
            if ((a.c + a.r) !== (b.c + b.r)) return (a.c + a.r) - (b.c + b.r);
            return a.r - b.r;
        });
        return {
            w: Math.ceil(maxX - minX + 2 * pad),
            h: Math.ceil(maxY - minY + 2 * pad),
            ox: -minX + pad,
            oy: -minY + pad,
            sorted,
            label: labels[i],
        };
    });

    const totalW = subs.reduce((acc, s) => acc + s.w, 0) + gap * (subs.length - 1);
    const maxH = Math.max(...subs.map(s => s.h)) + labelH;

    // Signature globale unique pour le fingerprint audit
    const sig = assemblages.map(a => a.cubes.map(c => `${c.c}${c.r}${c.l}`).join('')).join('_').slice(0, 30);

    let svg = `<svg id="c${sig}" viewBox="0 0 ${totalW} ${maxH}" xmlns="http://www.w3.org/2000/svg" style="max-width:${totalW}px;display:block;margin:4px auto">`;
    let xOffset = 0;
    for (const sub of subs) {
        svg += `<g transform="translate(${Math.round(xOffset + sub.ox)},${Math.round(sub.oy)})">`;
        for (const { c, r, l } of sub.sorted) svg += drawCube(c, r, l);
        svg += '</g>';
        if (sub.label) {
            svg += `<text x="${Math.round(xOffset + sub.w / 2)}" y="${maxH - 4}" text-anchor="middle" font-size="13" fill="currentColor">${sub.label}</text>`;
        }
        xOffset += sub.w + gap;
    }
    svg += '</svg>';
    return svg;
};

/** Convertit N assemblages en un seul élément image composite. */
const compositeToElement = (assemblages, labels, maxWidth = 'medium') => {
    const svg = buildCompositeSVG(assemblages, labels);
    return { type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth } };
};

// ─── Pool d'assemblages ───
// Chaque entrée : { cubes: [{c,r,l}], vol: nombre }

const ASSEMBLAGES_POOL = [
    // ─ Volume 4 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1}], vol: 4 },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:0,l:1}], vol: 4 },
    // ─ Volume 5 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1}], vol: 5 },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1}], vol: 5 },
    // ─ Volume 6 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:0,r:0,l:2}], vol: 6 },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0}], vol: 6 },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1}], vol: 6 },
    // ─ Volume 7 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1}], vol: 7 },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1},{c:0,r:1,l:1}], vol: 7 },
    // ─ Volume 8 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:0,r:1,l:1},{c:1,r:1,l:1}], vol: 8 },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:3,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1},{c:3,r:0,l:1}], vol: 8 },
    // ─ Volume 9 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1}], vol: 9 },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:2,l:0},{c:1,r:2,l:0},{c:2,r:2,l:0}], vol: 9 },
    // ─ Volume 10 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:0,r:1,l:1},{c:1,r:1,l:1}], vol: 10 },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:3,r:0,l:0},{c:4,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1},{c:3,r:0,l:1},{c:4,r:0,l:1}], vol: 10 },
    // ─ Volume 12 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1},{c:0,r:1,l:1},{c:1,r:1,l:1},{c:2,r:1,l:1}], vol: 12 },
    // ─ Volume 15 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:2,l:0},{c:1,r:2,l:0},{c:2,r:2,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1},{c:0,r:1,l:1},{c:1,r:1,l:1},{c:2,r:1,l:1}], vol: 15 },
];

// ─── Générateurs dynamiques d'assemblages ───

/** Parallélépipède rectangle (a × b × h). */
const genBoite = () => {
    let a, b, h;
    do {
        a = randInt(1, 4);
        b = randInt(1, 4);
        h = randInt(1, 4);
    } while (a * b * h < 2 || a * b * h > 20);
    const cubes = [];
    for (let l = 0; l < h; l++) {
        for (let r = 0; r < b; r++) {
            for (let c = 0; c < a; c++) {
                cubes.push({ c, r, l });
            }
        }
    }
    return { cubes, vol: a * b * h };
};

/** Base rectangulaire + colonne à un coin (forme en L). */
const genLShape = () => {
    const a = randInt(2, 4);
    const b = randInt(1, 3);
    const hColonne = randInt(1, 3);
    const vol = a * b + hColonne;
    if (vol > 20) return genLShape();
    const cubes = [];
    for (let r = 0; r < b; r++) {
        for (let c = 0; c < a; c++) {
            cubes.push({ c, r, l: 0 });
        }
    }
    for (let l = 1; l <= hColonne; l++) {
        cubes.push({ c: 0, r: 0, l });
    }
    return { cubes, vol };
};

/** Escalier : n marches de largeur w. */
const genEscalier = () => {
    const n = randInt(2, 4);
    const w = randInt(1, 2);
    const cubes = [];
    for (let l = 0; l < n; l++) {
        for (let c = 0; c < n - l; c++) {
            for (let r = 0; r < w; r++) {
                cubes.push({ c, r, l });
            }
        }
    }
    const vol = cubes.length;
    if (vol > 20) return genEscalier();
    return { cubes, vol };
};

/** Deux couches empilées de tailles différentes (pyramide tronquée). */
const genPyramide = () => {
    const a1 = randInt(2, 3), b1 = randInt(2, 3);
    const a2 = randInt(1, a1), b2 = randInt(1, b1);
    if (a1 === a2 && b1 === b2) return genPyramide();
    const vol = a1 * b1 + a2 * b2;
    if (vol > 20) return genPyramide();
    const cubes = [];
    for (let r = 0; r < b1; r++) {
        for (let c = 0; c < a1; c++) {
            cubes.push({ c, r, l: 0 });
        }
    }
    for (let r = 0; r < b2; r++) {
        for (let c = 0; c < a2; c++) {
            cubes.push({ c, r, l: 1 });
        }
    }
    return { cubes, vol };
};

/** Génère un assemblage aléatoire pondéré par la richesse de chaque type. */
const genOne = () => {
    const r = Math.random();
    if (r < 0.50) return genBoite();
    if (r < 0.75) return genLShape();
    if (r < 0.90) return genPyramide();
    return genEscalier();
};

/** Choisit deux assemblages de volumes différents (via générateurs dynamiques). */
const pickTwoDistinct = () => {
    const a = genOne();
    for (let i = 0; i < 20; i++) {
        const b = genOne();
        if (b.vol !== a.vol) return [a, b];
    }
    // Fallback sur le pool statique
    const fallback = ASSEMBLAGES_POOL.find(x => x.vol !== a.vol);
    return [a, fallback || ASSEMBLAGES_POOL[0]];
};

/** Signature canonique d'un assemblage (ensemble exact des positions). */
const sigAssemblage = (asm) => asm.cubes.map(k => `${k.c},${k.r},${k.l}`).sort().join(';');

/** Deux assemblages de MÊME volume mais de formes différentes (revue
 *  2026-07-31) : la leçon « le volume ne se voit pas, il se compte ».
 *  Égalité vérifiée par valeur, différence par signature canonique. */
const pickTwoEqual = () => {
    for (let i = 0; i < 200; i++) {
        const a = genOne();
        const b = genOne();
        if (a.vol === b.vol && sigAssemblage(a) !== sigAssemblage(b)) return [a, b];
    }
    // Repli déterministe vérifié : L 2×2 + colonne 2 (vol 6) vs escalier
    // 3-2-1 (vol 6) — formes très différentes, même compte.
    const elShape = { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1},{c:0,r:0,l:2}], vol: 6 };
    const esc = { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:0,r:0,l:2}], vol: 6 };
    return [elShape, esc];
};

// ═══════════════════════════════════════════
// EXERCICE — Comparer deux assemblages
// ═══════════════════════════════════════════

const genComparerQcm = () => {
    // ~30 % de tirages à volumes ÉGAUX (revue 2026-07-31) : « Ils ont le même
    // volume » n'était JAMAIS la bonne réponse (pickTwoDistinct l'excluait par
    // construction) — deux options mortes sur quatre, l'élève tranchait au
    // jugé entre A et B sans compter. « On ne peut pas savoir » reste
    // volontairement toujours faux : compter décide toujours.
    const egaux = Math.random() < 0.3;
    const [a, b] = egaux ? pickTwoEqual() : pickTwoDistinct();
    const texte = `<p>Les assemblages ci-dessous sont formés de cubes d'arête 1 cm. Quel assemblage a le plus grand volume\u00a0?</p>{{element:0}}`;
    const elAdd = { version: 1, elements: [compositeToElement([a, b], ['Assemblage A', 'Assemblage B'], 'medium')] };

    const correct = a.vol === b.vol
        ? 'Ils ont le même volume'
        : (a.vol > b.vol ? 'Assemblage A' : 'Assemblage B');

    const choices = [
        'Assemblage A',
        'Assemblage B',
        'Ils ont le même volume',
        'On ne peut pas savoir',
    ];

    const payload = makeQcm(choices, correct, texte, { ...opts(L_COMP), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'comparer_volumes';
    return payload;
};

const genComparerTrou = () => {
    const [a, b] = pickTwoDistinct();
    const texte = `<p>Chaque cube a une arête de 1 cm. Compte les cubes de chaque assemblage et compare.</p>{{element:0}}`;
    const elAdd = { version: 1, elements: [compositeToElement([a, b], ['Assemblage A', 'Assemblage B'], 'medium')] };

    const plusGrand = a.vol > b.vol ? 'A' : 'B';
    const phrase = `L'assemblage A contient {{${a.vol}}} cm³.\n`
        + `L'assemblage B contient {{${b.vol}}} cm³.\n`
        + `L'assemblage {{${plusGrand}}} a le plus grand volume.`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_COMP), mode: 'saisie', strict: false, elements_additionnels: elAdd });
    payload._meta.generatorKey = 'comparer_volumes';
    return payload;
};

// Refonte bilan prof D3 « plus tard » (arbitré 2026-07-27) : l'ancien 4 ↔ 4
// en bijection offrait la dernière paire par élimination, sans aucune
// valeur-piège de comptage. Gabarit 2 sources / 4 cibles :
//   · 2 assemblages COMPOSÉS de types différents (L, escalier, pyramide — les
//     boîtes pleines restent au QCM et aux trous : sans l'erreur de visibilité,
//     écartée par arbitrage, une boîte ne produit aucun leurre) ;
//   · 1 leurre par assemblage, code boite_completee : « étage du bas × nombre
//     d'étages » ≡ « il complète mentalement la boîte englobante » — sur ces
//     formes l'étage du bas remplit l'empreinte, les deux récits d'élève
//     donnent LA MÊME valeur (sur-comptage documenté, Battista & Clements).
// Gardes anti-doublon par RETIRAGE : leurre = volume d'un des deux assemblages
// ou = l'autre leurre → on retire le couple.

/** Volume « boîte complétée » : étage du bas × nombre d'étages. */
const volBoiteCompletee = (asm) => {
    const etages = new Set(asm.cubes.map(k => k.l)).size;
    const bas = asm.cubes.filter(k => k.l === 0).length;
    return bas * etages;
};

const genComparerRelier = () => {
    const formes = [genLShape, genEscalier, genPyramide];
    let choix = null;
    for (let t = 0; t < 60 && !choix; t++) {
        const [f1, f2] = shuffle([...formes]).slice(0, 2);
        const a1 = f1();
        const a2 = f2();
        if (a1.vol === a2.vol) continue;
        const l1 = volBoiteCompletee(a1);
        const l2 = volBoiteCompletee(a2);
        // chaque leurre doit être un vrai sur-comptage, distinct des deux
        // volumes corrects et de l'autre leurre
        const vols = [a1.vol, a2.vol];
        if (l1 === l2 || vols.includes(l1) || vols.includes(l2)) continue;
        choix = { items: [a1, a2], leurres: [l1, l2] };
    }
    if (!choix) {
        // Repli déterministe vérifié : escalier 3-2-1 (vol 6, leurre 9) +
        // pyramide 3×2 + 2×1 (vol 8, leurre 12) — 4 valeurs distinctes.
        const esc = { cubes: [{ c: 0, r: 0, l: 0 }, { c: 1, r: 0, l: 0 }, { c: 2, r: 0, l: 0 }, { c: 0, r: 0, l: 1 }, { c: 1, r: 0, l: 1 }, { c: 0, r: 0, l: 2 }], vol: 6 };
        const pyr = { cubes: [{ c: 0, r: 0, l: 0 }, { c: 1, r: 0, l: 0 }, { c: 2, r: 0, l: 0 }, { c: 0, r: 1, l: 0 }, { c: 1, r: 1, l: 0 }, { c: 2, r: 1, l: 0 }, { c: 0, r: 0, l: 1 }, { c: 1, r: 0, l: 1 }], vol: 8 };
        choix = { items: [esc, pyr], leurres: [9, 12] };
    }

    const labels = ['A', 'B'];
    const pairs = choix.items.map((item, i) => ({
        source: `Assemblage ${labels[i]}`,
        target: `${item.vol} cm³`,
    }));
    const leurres = choix.leurres.map(v => ({ target: `${v} cm³`, code: 'boite_completee' }));

    const texte = `<p>Chaque cube a une arête de 1 cm. Relie chaque assemblage à son volume. `
        + `Attention : certains volumes ne correspondent à aucun assemblage.</p>{{element:0}}`;
    const elAdd = { version: 1, elements: [compositeToElement(choix.items, choix.items.map((_, i) => `Assemblage ${labels[i]}`), 'large')] };

    const payload = makeRelier(pairs, texte, {
        ...opts(L_COMP), cibles_leurres: leurres, elements_additionnels: elAdd,
    });
    payload._meta.generatorKey = 'comparer_volumes';
    payload._meta.params = {
        vols: choix.items.map(i => i.vol),
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    comparer_volumes_qcm_unique:       genComparerQcm,
    comparer_volumes_texte_trou_saisie: genComparerTrou,
    comparer_volumes_relier:           genComparerRelier,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'comparer_volumes', label: L_COMP, formatsList: buildFormats('comparer_volumes') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
